import type { CtaEntryPoint } from '../../domain/modal'
import type { DeepReadonly, Ref } from 'vue'
import type { PhantomBridgeHandoffState } from './bridge-handoff'

export type PhantomCallbackUiState = 'pending' | 'ok' | 'awaiting-handoff' | 'error'

export interface PhantomCallbackResult {
  state: PhantomCallbackUiState
  heading: string
  detail: string
  redirect: { path: string; query?: Record<string, string> } | null
  requiresHandoff: boolean
}

export interface PhantomApi {
  isDeeplinkEnvironment: () => boolean
  isSuspendedError: (error: unknown) => boolean
  recordDiagnostic: (event: string, data?: Record<string, unknown>) => void
  processCallback: (
    search: string,
    onProgress?: (detail: string) => void
  ) => Promise<PhantomCallbackResult>
  bridgeHandoffState: DeepReadonly<Ref<PhantomBridgeHandoffState>>
  canTriggerBridgeHandoff: () => boolean
  canLaunchBridgeHandoffFallback: () => boolean
  launchPreparedBridgeHandoff: () => Promise<never>
  launchBridgeHandoffFallback: () => Promise<never>
  retryBridgeHandoffPreparation: () => Promise<void>
  markBridgeHandoffLaunchFailed: (fallbackAvailable?: boolean) => void
  clearBridgeHandoff: () => void
}

export interface PhantomBindingsOwner {
  acquire: (hooks: PhantomMountHooks) => Promise<void>
  release: () => void
  dispose: () => void
}

export interface PhantomMountHooks {
  openFromCta: (entryPoint: CtaEntryPoint, signal?: AbortSignal) => Promise<void>
  connectSolanaWallet: () => Promise<void>
  trackEvent: (eventName: string, payload?: Record<string, unknown>) => void
}

let installedApi: PhantomApi | null = null
let installedBindings: PhantomBindingsOwner | null = null

export const installPhantom = (api: PhantomApi, bindings: PhantomBindingsOwner): void => {
  installedApi = api
  installedBindings = bindings
}

export const getPhantom = (): PhantomApi => {
  if (!installedApi) throw new Error('crosschain_phantom_not_installed')
  return installedApi
}

export const getPhantomBindings = (): PhantomBindingsOwner => {
  if (!installedBindings) throw new Error('crosschain_phantom_bindings_not_installed')
  return installedBindings
}
